import { Box, styled, Tooltip } from '@mui/material';
import { getQueryKey } from '@trpc/react-query';
import Button from 'components/Button';
import InteractiveTable, {
  Column as TableColumn,
} from 'components/InteractiveTable';
import { BankAccountOperationStatus } from 'further-types/lp/bank-reconciliation';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'further-ui/components/Layout';
import { useDisclosure, usePagination } from 'further-ui/hooks';
import {
  BankAccountOperationSourceLabels,
  BankAccountOperationStatusLabels,
} from 'further-ui/labels';
import { dateToLabel, numberToCurrencyString } from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useState } from 'react';

type Props = {
  bankAccount: {
    id: string;
    vehicleId: string;
  };
};

const Reconciled = styled(Box)(({ theme }) => ({
  color: theme.palette.text.rootColor,
}));
const Unreconciled = styled(Box)(({ theme }) => ({
  color: theme.palette.error.main,
}));

const BankStatement: React.FC<Props> = ({ bankAccount }) => {
  const pageId = `bank-statement-${bankAccount.id}`;
  const deleteDisclosure = useDisclosure();
  const [selectedOperations, setSelectedOperations] = useState<Array<string>>(
    [],
  );
  const pagination = usePagination({ id: pageId });

  const [data] = api.bankAccount.listBankAccountOperations.useSuspenseQuery({
    vehicleId: bankAccount.vehicleId,
    bankAccountId: bankAccount.id,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    orderBy: 'date',
    order: 'desc',
  });

  const [canDeleteData] =
    api.bankAccount.canDeleteBankAccountOperations.useSuspenseQuery({
      vehicleId: bankAccount.vehicleId,
      bankAccountId: bankAccount.id,
    });

  const deleteOperations =
    api.bankAccount.deleteBankAccountOperations.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Operations deleted successfully.',
        invalidations: [getQueryKey(api.bankAccount.listBankAccountOperations)],
      }),
    );

  const handleDelete = () => {
    if (deleteOperations.isPending || !selectedOperations.length) return;

    deleteDisclosure.onClose();
    deleteOperations.mutate({
      bankAccountId: bankAccount.id,
      operationIds: selectedOperations,
      vehicleId: bankAccount.vehicleId,
    });
  };

  const canDelete = canDeleteData?.canDelete ?? true;
  const deleteDisabledReason = canDeleteData?.reason;

  const columns: Array<TableColumn> = [
    {
      field: 'date',
      headerName: 'Date',
      renderCell: ({ row }) => dateToLabel(row.date),
      flex: 1,
    },
    {
      field: 'counterparty',
      headerName: 'Counterparty',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Details',
      flex: 1,
    },
    {
      field: 'spent',
      headerName: 'Spent',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.spent, {
          fallback: '-',
          currency: row.accountCurrency,
        }),
      flex: 1,
    },
    {
      field: 'received',
      headerName: 'Received',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.received, {
          fallback: '-',
          currency: row.accountCurrency,
        }),
      flex: 1,
    },
    {
      field: 'runningBalance',
      headerName: 'Running balance',
      renderCell: ({ row }) =>
        numberToCurrencyString(row.runningBalance, {
          currency: row.accountCurrency,
        }),
      flex: 1,
    },
    {
      field: 'source',
      headerName: 'Source',
      renderCell: ({ row }) => BankAccountOperationSourceLabels[row.source],
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: ({ row }) =>
        row.status === BankAccountOperationStatus.Reconciled ? (
          <Reconciled>
            {BankAccountOperationStatusLabels[row.status]}
          </Reconciled>
        ) : (
          <Unreconciled>
            {BankAccountOperationStatusLabels[row.status]}
          </Unreconciled>
        ),
      flex: 1,
    },
  ];

  return (
    <>
      <Column spacing="lg">
        {!!selectedOperations.length && (
          <Row justify="flex-end">
            {!canDelete && deleteDisabledReason ? (
              <Tooltip title={deleteDisabledReason}>
                <span>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={deleteDisclosure.onOpen}
                    loading={deleteOperations.isPending}
                    disabled={!canDelete}
                    size="large"
                  >
                    Delete selected statement lines
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                variant="contained"
                color="error"
                onClick={deleteDisclosure.onOpen}
                loading={deleteOperations.isPending}
                disabled={!canDelete}
                size="large"
              >
                Delete selected statement lines
              </Button>
            )}
          </Row>
        )}

        <InteractiveTable
          selectable
          disableTableConfiguration
          disableSorting
          id={pageId}
          columns={columns}
          rows={data?.results ?? []}
          pagination={pagination}
          rowCount={data?.totalCount ?? 0}
          onRowSelection={setSelectedOperations}
          scrollable
        />
      </Column>
      <ConfirmDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        title="Are you sure?"
        content="This will change your overall statement balance. This is generally only required to remove duplicate statement lines."
        actions={{
          confirm: {
            onClick: handleDelete,
            label: 'Delete',
            color: 'error',
          },
        }}
      />
    </>
  );
};

export default BankStatement;
